import { useState } from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import { Badge, Button, ProviderLogo } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useSetDefaultCredential, useStartOAuth } from '@/hooks/useIdentityCredentials';
import AccountRow from './AccountRow';
import AddApiKeyModal from './AddApiKeyModal';
import AddAwsRoleModal from './AddAwsRoleModal';
import ConnectOAuthTokenModal from './ConnectOAuthTokenModal';
import DeviceAuthModal from './DeviceAuthModal';
import OrgAvailabilityHint from './OrgAvailabilityHint';
import {
  authMethodLabel,
  deriveLogoSlug,
  deriveProviderStatus,
  isDeviceStart,
  openIdentityOAuthUrl,
  providerStatusLabel,
  providerStatusVariant,
} from './identity-lib';
import type {
  Credential,
  CredentialAuthType,
  OAuthDeviceStart,
  ProviderSummary,
} from '@/types/identity';
import type { Scope } from './scope';

interface Props {
  provider: ProviderSummary;
  /** The row's single auth method — drives the connect button. */
  authType: CredentialAuthType;
  credentials: Credential[];
  canManage: boolean;
  scope: Scope;
  /** Append the auth-method label so duplicate-provider rows are distinguishable. */
  showMethod: boolean;
  /** Show the inline `Also available at org level` hint. */
  alsoAtOrg: boolean;
}

export default function ProviderCard({
  provider,
  authType,
  credentials,
  canManage,
  scope,
  showMethod,
  alsoAtOrg,
}: Props) {
  const { error: showError } = useToast();
  const startOAuth = useStartOAuth();
  const setDefault = useSetDefaultCredential();
  const [apiKeyModal, setApiKeyModal] = useState(false);
  const [oauthManualModal, setOauthManualModal] = useState(false);
  const [awsRoleModal, setAwsRoleModal] = useState(false);
  const [deviceStart, setDeviceStart] = useState<OAuthDeviceStart | null>(null);

  const accountCount = credentials.length;
  const hasDefault = credentials.some((c) => c.metadata.isDefault);
  const needsDefaultPick = canManage && accountCount >= 2 && !hasDefault;
  // Backend's provider.status is a cross-tier aggregate that can lag the
  // per-scope credentials we're actually rendering. Derive from the local
  // list so the header badge matches the account rows below.
  const displayedStatus = deriveProviderStatus(credentials, provider.status);

  const startProviderOAuth = async () => {
    // OAuth start is user- or space-scope; org has no OAuth-start route
    // and ConnectAnotherFooter hides the button there, so this is
    // unreachable when scope.kind === 'org'.
    if (scope.kind === 'org') return;
    try {
      const result = await startOAuth.mutateAsync({
        scope: scope.kind === 'user' ? 'user' : { spaceId: scope.spaceId },
        provider: provider.id,
        body: {
          redirectUri: `${window.location.origin}/oauth/identity-callback`,
          returnUrl: `${window.location.origin}/oauth/connected`,
          // Instance-bound rows (interactive-OAuth MCP servers) carry the
          // server id; forward it so the backend resolves the per-server app
          // creds. Provider-wide rows leave it undefined — unchanged behavior.
          providerInstanceId: provider.providerInstanceId,
        },
      });
      // Device-authorization providers (aws-sso) hand back a verification URL +
      // user code instead of an authorizationUrl — route those to the modal.
      if (isDeviceStart(result)) {
        setDeviceStart(result);
      } else {
        openIdentityOAuthUrl(result.authorizationUrl, {
          provider: provider.id,
          scope: scope.kind,
        });
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not start OAuth');
    }
  };

  const pickFirstAsDefault = async () => {
    const first = credentials[0];
    if (!first) return;
    try {
      await setDefault.mutateAsync(first.credId);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Set-default failed');
    }
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <div className="flex items-center gap-4 pl-6 pr-4 py-3 border-b border-border bg-muted/30">
        <ProviderLogo
          slug={deriveLogoSlug(provider.id, provider.displayName)}
          displayName={provider.displayName}
          className="h-8 w-8 [filter:drop-shadow(0_0_10px_rgb(255_255_255/0.18))]"
          grayscale
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-foreground truncate">
              {provider.displayName}
            </h3>
            <Badge variant={providerStatusVariant(displayedStatus)}>
              {providerStatusLabel(displayedStatus)}
            </Badge>
            {alsoAtOrg && <OrgAvailabilityHint />}
          </div>
          {(showMethod || accountCount > 0) && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {[
                showMethod ? authMethodLabel(authType) : null,
                accountCount > 0
                  ? `${accountCount} ${accountCount === 1 ? 'account' : 'accounts'}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
      </div>

      {needsDefaultPick && (
        <div className="flex items-start gap-3 px-6 py-3 border-b border-border bg-status-warning-bg/30">
          <AlertTriangle className="h-4 w-4 text-status-warning-fg shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="text-foreground font-medium">Pick a default account</p>
            <p className="text-muted-foreground text-xs mt-0.5">
              Tools using {provider.displayName} need to know which account to
              call. Choose one as the default, or set it from any row's menu.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={pickFirstAsDefault}
            isLoading={setDefault.isPending}
          >
            Pick first
          </Button>
        </div>
      )}

      <div className="divide-y divide-border">
        {credentials.map((cred) => (
          <AccountRow
            key={cred.credId}
            credential={cred}
            canManage={canManage}
            hasSiblings={accountCount > 1}
            scope={scope}
          />
        ))}
      </div>

      {canManage && (
        <ConnectAnotherFooter
          authType={authType}
          scope={scope}
          onStartOAuth={startProviderOAuth}
          onStartApiKey={() => setApiKeyModal(true)}
          onStartOAuthManual={() => setOauthManualModal(true)}
          onStartAwsRole={() => setAwsRoleModal(true)}
          isOAuthPending={startOAuth.isPending}
        />
      )}

      {apiKeyModal && (
        <AddApiKeyModal
          isOpen={apiKeyModal}
          onClose={() => setApiKeyModal(false)}
          provider={provider}
          scope={scope}
        />
      )}
      {oauthManualModal && (
        <ConnectOAuthTokenModal
          isOpen={oauthManualModal}
          onClose={() => setOauthManualModal(false)}
          provider={provider}
          scope={scope}
        />
      )}
      {awsRoleModal && (
        <AddAwsRoleModal
          isOpen={awsRoleModal}
          onClose={() => setAwsRoleModal(false)}
          provider={provider}
          scope={scope}
        />
      )}
      {deviceStart && (
        <DeviceAuthModal
          isOpen={true}
          onClose={() => setDeviceStart(null)}
          provider={provider}
          device={deviceStart}
        />
      )}
    </div>
  );
}

interface FooterProps {
  authType: CredentialAuthType;
  scope: Scope;
  onStartOAuth: () => void;
  onStartApiKey: () => void;
  onStartOAuthManual: () => void;
  onStartAwsRole: () => void;
  isOAuthPending: boolean;
}

function ConnectAnotherFooter({
  authType,
  scope,
  onStartOAuth,
  onStartApiKey,
  onStartOAuthManual,
  onStartAwsRole,
  isOAuthPending,
}: FooterProps) {
  // Each card represents a single auth method, so the footer adds another
  // account via that same method. The new identity API has no org-level
  // OAuth-start route — hide the button on the org tab (it'd 404). api-key
  // and oauth-manual exist on all three scopes.
  const isOAuth = authType === 'oauth2';
  if (isOAuth && scope.kind === 'org') return null;
  // IAM roles are space/org only.
  if (authType === 'aws_role' && scope.kind === 'user') return null;

  const { onClick, label } =
    authType === 'oauth2'
      ? { onClick: onStartOAuth, label: 'Connect another account' }
      : authType === 'api_key'
        ? { onClick: onStartApiKey, label: 'Add another API token' }
        : authType === 'aws_role'
          ? { onClick: onStartAwsRole, label: 'Add another IAM role' }
          : { onClick: onStartOAuthManual, label: 'Add another OAuth token' };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isOAuth && isOAuthPending}
      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors border-t border-border disabled:opacity-60"
    >
      <Plus className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </button>
  );
}
